import React, { useEffect, useState } from 'react';
import MoodPicker from './MoodPicker';
import StrainPicker from './StrainPicker';
import AchievementUnlockModal from './AchievementUnlockModal';
import { getConsumptionOptions, getStrains, getBrands, saveConsumptionLog } from '../api';

const BUY_AGAIN_LABELS = {
  1: 'Never Again',
  3: 'Passable but Buyable',
  5: 'Always',
};

const SIDE_EFFECT_SEVERITY_LABELS = {
  1: 'Mild',
  3: 'Moderate',
  5: 'High',
};

const EMPTY_EFFECTS = {
  headache: 'no',
  paranoia: 'no',
  dizzy: 'no',
  sleepy: 'no',
  stuffy: 'no',
  cough: 'no',
  dryMouth: 'no',
  dryEye: 'no',
  redEye: 'no',
  anxiety: 'no',
};

function isYesEffect(value) {
  return typeof value === 'number' && value >= 1 && value <= 5;
}

function effectPayloadValue(value) {
  return isYesEffect(value) ? value : 0;
}

function focusField(fieldId) {
  const tryFocus = () => {
    const el = document.getElementById(fieldId);
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const target = el.matches('input, select, textarea, button')
      ? el
      : el.querySelector(
          'input:not([type="hidden"]), select, textarea, button:not(:disabled)'
        ) || el;
    try {
      target.focus({ preventScroll: true });
    } catch (_err) {
      target.focus?.();
    }
    return document.activeElement === target || el.contains(document.activeElement);
  };

  window.requestAnimationFrame(() => {
    if (!tryFocus()) {
      window.setTimeout(tryFocus, 120);
    }
  });
}

function MissingFieldsModal({ messages, onClose }) {
  if (!messages?.length) return null;

  const heading =
    messages.length === 1 ? 'Missing required field' : 'Missing required fields';

  return (
    <div className="achievement-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="achievement-modal missing-fields-modal"
        role="dialog"
        aria-labelledby="missing-fields-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="achievement-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h3 id="missing-fields-title">{heading}</h3>
        <p className="subtitle">Fill these in, then try Save again.</p>
        <ul className="missing-fields-list">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
        <button type="button" autoFocus onClick={onClose}>
          OK
        </button>
      </div>
    </div>
  );
}

function SmokeForm({ user, onSaved, onBack, consumptionStyle = 'Smoke' }) {
  const isBong = consumptionStyle === 'Water Pipe (Bong)';
  const isDab = consumptionStyle === 'Concentrates/Dab';
  const isVape = consumptionStyle === 'Vape';
  const isSmoke = consumptionStyle === 'Smoke';
  const isEdible = consumptionStyle === 'Eat/Drink';
  const isTopical = consumptionStyle === 'Topical';
  const isPill = consumptionStyle === 'Pill/Capsule';
  const isHeatNotBurn = consumptionStyle === 'Heat not Burn';
  const showInfused = isSmoke || isBong || isHeatNotBurn;
  const showHow = isSmoke || isBong || isDab || isVape;
  const showStrength = isEdible || isTopical || isPill;
  const showAlone = !isPill;
  const showMoods = user?.showMoods !== false;
  const [options, setOptions] = useState({
    how: [],
    bongHow: [],
    dabHow: [],
    dabStyles: [],
    vapeHow: [],
    edibleHow: [],
    edibleAmounts: [],
    heatNotBurnTemps: [],
    amounts: [],
    groupSizes: [],
    sideEffects: [],
    moods: [],
    strainTypes: [],
  });
  const [strains, setStrains] = useState([]);
  const [brands, setBrands] = useState([]);
  const [howMethod, setHowMethod] = useState('');
  const [howOther, setHowOther] = useState('');
  const [howStyle, setHowStyle] = useState('');
  const [enjoyIt, setEnjoyIt] = useState('');
  const [strength, setStrength] = useState('');
  const [brandName, setBrandName] = useState('');
  const [customBrand, setCustomBrand] = useState('');
  const [strainName, setStrainName] = useState('');
  const [customStrain, setCustomStrain] = useState('');
  const [strainType, setStrainType] = useState('');
  const [infused, setInfused] = useState('no');
  const [amountChoice, setAmountChoice] = useState('');
  const [amountOther, setAmountOther] = useState('');
  const [startingMoods, setStartingMoods] = useState([]);
  const [endingMoods, setEndingMoods] = useState([]);
  const [location, setLocation] = useState('');
  const [alone, setAlone] = useState('');
  const [groupSize, setGroupSize] = useState('');
  const [notes, setNotes] = useState('');
  const [privateNotes, setPrivateNotes] = useState('');
  const [buyAgain, setBuyAgain] = useState(3);
  const [effects, setEffects] = useState(EMPTY_EFFECTS);
  const [error, setError] = useState('');
  const [invalidFields, setInvalidFields] = useState([]);
  const [missingAlert, setMissingAlert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [pendingLogs, setPendingLogs] = useState(null);

  useEffect(() => {
    getConsumptionOptions()
      .then(setOptions)
      .catch(() => {});
    getStrains()
      .then((data) => setStrains(data.strains || []))
      .catch(() => {});
    getBrands()
      .then((data) => setBrands(data.brands || []))
      .catch(() => {});
  }, []);

  const strainIsOther = strainName === 'Other';
  const brandIsOther = brandName === 'Other';
  const showGroupSize = showAlone && alone === 'no';
  const howOptions = isBong
    ? options.bongHow || []
    : isDab
      ? options.dabHow || []
      : isVape
        ? options.vapeHow || []
        : options.how || [];

  const isInvalid = (fieldId) => invalidFields.includes(fieldId);
  const clearInvalid = (fieldId) => {
    setInvalidFields((current) => current.filter((id) => id !== fieldId));
  };
  const groupClass = (fieldId, extra = '') =>
    `form-group${extra ? ` ${extra}` : ''}${isInvalid(fieldId) ? ' field-invalid' : ''}`;

  const collectMissingFields = () => {
    const missing = [];
    const push = (id, message) => missing.push({ id, message });

    if (showHow && !howMethod) {
      push('howMethod', 'Choose how you consumed.');
    }
    if (isSmoke && howMethod === 'Other' && !howOther.trim()) {
      push('howOther', 'Describe how you consumed.');
    }
    if (isDab && !howStyle) {
      push('howStyle', 'Choose a concentrate style.');
    }
    if (isEdible && !howMethod) {
      push('edibleHow', 'Choose a type of edible.');
    }
    if (isEdible && !enjoyIt) {
      push('enjoyIt', 'Say whether you enjoy it.');
    }
    if (isEdible && !amountChoice) {
      push('edibleAmount', 'Choose how much.');
    }
    if (isHeatNotBurn && !howMethod) {
      push('temperature', 'Choose a temperature.');
    }
    if (!brandName) {
      push('brandName', 'Choose a brand, or Other to add one.');
    }
    if (brandIsOther && !customBrand.trim()) {
      push('customBrand', 'Enter the brand name.');
    }
    if (!strainName) {
      push(
        'strainName',
        'Type to find a strain, then choose it from the list. Use Other if it is not there.'
      );
    }
    if (strainIsOther && !customStrain.trim()) {
      push('customStrain', 'Enter the strain name.');
    }
    if (strainIsOther && !strainType) {
      push('strainType', 'Choose a strain type.');
    }
    if (isSmoke && !amountChoice) {
      push('amountChoice', 'Choose an amount.');
    }
    if (isSmoke && amountChoice === 'Other' && !amountOther) {
      push('amountOther', 'Enter the amount in grams.');
    }
    if (showMoods && !startingMoods.length) {
      push('startingMood', 'Choose 1 to 3 starting moods.');
    }
    if (showMoods && !endingMoods.length) {
      push('endingMood', 'Choose 1 to 3 ending moods.');
    }
    if (showAlone && !alone) {
      push('alone', 'Say whether you are alone.');
    }
    if (showGroupSize && !groupSize) {
      push('groupSize', 'Choose a group size.');
    }

    return missing;
  };

  const dismissMissingAlert = () => {
    const fieldId = missingAlert?.fieldId || '';
    setMissingAlert(null);
    if (fieldId) {
      window.setTimeout(() => focusField(fieldId), 50);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const missing = collectMissingFields();
    if (missing.length) {
      setInvalidFields(missing.map((item) => item.id));
      setError(missing[0].message);
      setMissingAlert({
        fieldId: missing[0].id,
        messages: missing.map((item) => item.message),
      });
      return;
    }

    setInvalidFields([]);
    setMissingAlert(null);
    setSaving(true);
    try {
      const result = await saveConsumptionLog({
        userId: user.id,
        consumptionStyle,
        howMethod,
        howOther: isSmoke ? howOther : '',
        howStyle: isDab ? howStyle : '',
        enjoyIt: isEdible ? enjoyIt === 'yes' : null,
        strength: showStrength ? strength : '',
        brandName,
        customBrand: brandIsOther ? customBrand : '',
        strainName,
        customStrain: strainIsOther ? customStrain : '',
        strainType: strainIsOther ? strainType : '',
        infused: showInfused ? infused === 'yes' : null,
        amountChoice: isSmoke || isEdible ? amountChoice : '',
        amountOther: isSmoke ? amountOther : '',
        startingMoods: showMoods ? startingMoods : [],
        endingMoods: showMoods ? endingMoods : [],
        location,
        alone: showAlone ? alone === 'yes' : true,
        groupSize: showAlone && alone === 'no' ? groupSize : '',
        notes,
        privateNotes,
        buyAgain: Number(buyAgain),
        headache: effectPayloadValue(effects.headache),
        paranoia: effectPayloadValue(effects.paranoia),
        dizzy: effectPayloadValue(effects.dizzy),
        sleepy: effectPayloadValue(effects.sleepy),
        stuffy: effectPayloadValue(effects.stuffy),
        cough: effectPayloadValue(effects.cough),
        dryMouth: effectPayloadValue(effects.dryMouth),
        dryEye: effectPayloadValue(effects.dryEye),
        redEye: effectPayloadValue(effects.redEye),
        anxiety: effectPayloadValue(effects.anxiety),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
      const newlyEarned = result.achievements?.newlyEarned || [];
      if (newlyEarned.length) {
        setPendingLogs(result.logs || []);
        setUnlockedAchievements(newlyEarned);
      } else {
        onSaved(result.logs || []);
      }
    } catch (err) {
      setError(err.error || 'Unable to save this session');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container wide">
      <p className="brand">My Weed Buddy</p>
      <h1>{consumptionStyle}</h1>
      <p className="subtitle">
        {isSmoke ? 'Log this session. Amount is in grams.' : 'Log this session.'}
      </p>
      {error && <div className="error">{error}</div>}

      <form onSubmit={handleSubmit} noValidate>
        {showHow && (
          <div className={groupClass('howMethod')}>
            <label htmlFor="howMethod">How</label>
            {isBong ? <p className="field-note">Type of bong.</p> : null}
            {isDab ? <p className="field-note">Type of concentrate.</p> : null}
            {isVape ? <p className="field-note">Type of vape.</p> : null}
            <select
              id="howMethod"
              value={howMethod}
              onChange={(e) => {
                setHowMethod(e.target.value);
                clearInvalid('howMethod');
              }}
              aria-invalid={isInvalid('howMethod') || undefined}
              required
            >
              <option value="">Select</option>
              {howOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}
        {isSmoke && howMethod === 'Other' && (
          <div className={groupClass('howOther')}>
            <label htmlFor="howOther">Describe how</label>
            <input
              id="howOther"
              value={howOther}
              onChange={(e) => {
                setHowOther(e.target.value);
                clearInvalid('howOther');
              }}
              aria-invalid={isInvalid('howOther') || undefined}
              required
            />
          </div>
        )}

        {isDab && (
          <div className={groupClass('howStyle')}>
            <label htmlFor="howStyle">Style</label>
            <select
              id="howStyle"
              value={howStyle}
              onChange={(e) => {
                setHowStyle(e.target.value);
                clearInvalid('howStyle');
              }}
              aria-invalid={isInvalid('howStyle') || undefined}
              required
            >
              <option value="">Select</option>
              {(options.dabStyles || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {isEdible && (
          <div className={groupClass('edibleHow')}>
            <label htmlFor="edibleHow">Type of Edible</label>
            <select
              id="edibleHow"
              value={howMethod}
              onChange={(e) => {
                setHowMethod(e.target.value);
                clearInvalid('edibleHow');
              }}
              aria-invalid={isInvalid('edibleHow') || undefined}
              required
            >
              <option value="">Select</option>
              {(options.edibleHow || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {isEdible && (
          <div className={groupClass('enjoyIt')}>
            <label htmlFor="enjoyIt">Do you enjoy it?</label>
            <select
              id="enjoyIt"
              value={enjoyIt}
              onChange={(e) => {
                setEnjoyIt(e.target.value);
                clearInvalid('enjoyIt');
              }}
              aria-invalid={isInvalid('enjoyIt') || undefined}
              required
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
        )}

        {isEdible && (
          <div className={groupClass('edibleAmount')}>
            <label htmlFor="edibleAmount">How much?</label>
            <select
              id="edibleAmount"
              value={amountChoice}
              onChange={(e) => {
                setAmountChoice(e.target.value);
                clearInvalid('edibleAmount');
              }}
              aria-invalid={isInvalid('edibleAmount') || undefined}
              required
            >
              <option value="">Select</option>
              {(options.edibleAmounts || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {isHeatNotBurn && (
          <div className={groupClass('temperature')}>
            <label htmlFor="temperature">Temperature</label>
            <select
              id="temperature"
              value={howMethod}
              onChange={(e) => {
                setHowMethod(e.target.value);
                clearInvalid('temperature');
              }}
              aria-invalid={isInvalid('temperature') || undefined}
              required
            >
              <option value="">Select</option>
              {(options.heatNotBurnTemps || []).map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        )}

        {showStrength && (
          <div className="form-group">
            <label htmlFor="strength">Strength</label>
            <p className="field-note">Optional.</p>
            <input
              id="strength"
              value={strength}
              onChange={(e) => setStrength(e.target.value)}
              maxLength={120}
            />
          </div>
        )}

        <div className={groupClass('brandName')}>
          <label htmlFor="brandName">Brand</label>
          <p className="field-note">Type to find a brand. Choose Other to add one.</p>
          <StrainPicker
            id="brandName"
            strains={brands}
            value={brandName}
            onSelect={(name, typed) => {
              setBrandName(name);
              if (name === 'Other' && typed) setCustomBrand(typed);
              clearInvalid('brandName');
            }}
            placeholder="Type to find a brand"
            emptyLabel="No matching brands"
          />
        </div>
        {brandIsOther && (
          <div className={groupClass('customBrand')}>
            <label htmlFor="customBrand">Brand name</label>
            <input
              id="customBrand"
              value={customBrand}
              onChange={(e) => {
                setCustomBrand(e.target.value);
                clearInvalid('customBrand');
              }}
              aria-invalid={isInvalid('customBrand') || undefined}
              required
            />
          </div>
        )}

        <div className={groupClass('strainName')}>
          <label htmlFor="strainName">Strain</label>
          <p className="field-note">Type to find a strain. Choose Other if it is not in the list.</p>
          <StrainPicker
            id="strainName"
            strains={strains}
            value={strainName}
            onSelect={(name, typed) => {
              setStrainName(name);
              if (name === 'Other' && typed) setCustomStrain(typed);
              clearInvalid('strainName');
            }}
          />
        </div>
        {strainIsOther && (
          <>
            <div className={groupClass('customStrain')}>
              <label htmlFor="customStrain">Strain name</label>
              <input
                id="customStrain"
                value={customStrain}
                onChange={(e) => {
                  setCustomStrain(e.target.value);
                  clearInvalid('customStrain');
                }}
                aria-invalid={isInvalid('customStrain') || undefined}
                required
              />
            </div>
            <div className={groupClass('strainType')}>
              <label htmlFor="strainType">Type</label>
              <select
                id="strainType"
                value={strainType}
                onChange={(e) => {
                  setStrainType(e.target.value);
                  clearInvalid('strainType');
                }}
                aria-invalid={isInvalid('strainType') || undefined}
                required
              >
                <option value="">Select</option>
                {options.strainTypes.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {showInfused && (
          <div className="form-group">
            <label htmlFor="infused">Is the item infused?</label>
            <select
              id="infused"
              value={infused}
              onChange={(e) => setInfused(e.target.value)}
              required
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
        )}

        {isSmoke && (
          <>
            <div className={groupClass('amountChoice')}>
              <label htmlFor="amountChoice">Amount</label>
              <p className="field-note">Amount is in grams.</p>
              <select
                id="amountChoice"
                value={amountChoice}
                onChange={(e) => {
                  setAmountChoice(e.target.value);
                  clearInvalid('amountChoice');
                }}
                aria-invalid={isInvalid('amountChoice') || undefined}
                required
              >
                <option value="">Select</option>
                {options.amounts.map((option) => (
                  <option key={option} value={option}>
                    {option} g
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
            </div>
            {amountChoice === 'Other' && (
              <div className={groupClass('amountOther')}>
                <label htmlFor="amountOther">Amount in grams</label>
                <input
                  id="amountOther"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountOther}
                  onChange={(e) => {
                    setAmountOther(e.target.value);
                    clearInvalid('amountOther');
                  }}
                  aria-invalid={isInvalid('amountOther') || undefined}
                  required
                />
              </div>
            )}
          </>
        )}

        {showMoods ? (
          <>
            <MoodPicker
              id="startingMood"
              label="Starting mood"
              value={startingMoods}
              groups={options.moods}
              onChange={(next) => {
                setStartingMoods(next);
                if (next.length) clearInvalid('startingMood');
              }}
              invalid={isInvalid('startingMood')}
            />
            <MoodPicker
              id="endingMood"
              label="Ending mood"
              value={endingMoods}
              groups={options.moods}
              onChange={(next) => {
                setEndingMoods(next);
                if (next.length) clearInvalid('endingMood');
              }}
              invalid={isInvalid('endingMood')}
            />
          </>
        ) : null}

        <div className="form-group">
          <label htmlFor="location">Location</label>
          <input
            id="location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        {showAlone && (
          <>
            <div className={groupClass('alone')}>
              <label htmlFor="alone">Are you alone?</label>
              <select
                id="alone"
                value={alone}
                onChange={(e) => {
                  setAlone(e.target.value);
                  clearInvalid('alone');
                }}
                aria-invalid={isInvalid('alone') || undefined}
                required
              >
                <option value="">Select</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            {showGroupSize && (
              <div className={groupClass('groupSize')}>
                <label htmlFor="groupSize">Group size</label>
                <select
                  id="groupSize"
                  value={groupSize}
                  onChange={(e) => {
                    setGroupSize(e.target.value);
                    clearInvalid('groupSize');
                  }}
                  aria-invalid={isInvalid('groupSize') || undefined}
                  required
                >
                  <option value="">Select</option>
                  {options.groupSizes.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        <div className="form-group">
          <label htmlFor="buyAgain">Will you buy again?</label>
          <input
            id="buyAgain"
            className="buy-again-slider"
            type="range"
            min="1"
            max="5"
            step="1"
            value={buyAgain}
            onChange={(e) => setBuyAgain(Number(e.target.value))}
          />
          <div className="buy-again-marks">
            <span>1 Never Again</span>
            <span>3 Passable but Buyable</span>
            <span>5 Always</span>
          </div>
          <p className="field-note">
            Selected: {buyAgain}
            {BUY_AGAIN_LABELS[buyAgain] ? ` — ${BUY_AGAIN_LABELS[buyAgain]}` : ''}
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <p className="field-note">Visible to friends.</p>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
          />
        </div>

        <div className="form-group">
          <label htmlFor="privateNotes">Private notes</label>
          <p className="field-note">Only you can see these. Never shared with friends.</p>
          <textarea
            id="privateNotes"
            value={privateNotes}
            onChange={(e) => setPrivateNotes(e.target.value)}
            rows={4}
          />
        </div>

        <h2>Side effects</h2>
        <p className="subtitle">Defaults to No. Click Yes only if you had that effect, then rate severity.</p>
        {(options.sideEffects || []).map((item) => {
          const effectId = `effect-${item.key}`;
          return (
            <div className={groupClass(effectId, 'yn-row')} key={item.key} id={effectId}>
              <span>{item.label}</span>
              <div className="yn-options">
                <label>
                  <input
                    type="radio"
                    name={item.key}
                    value="yes"
                    checked={isYesEffect(effects[item.key])}
                    onChange={() => {
                      setEffects((current) => ({ ...current, [item.key]: 3 }));
                      clearInvalid(effectId);
                    }}
                  />{' '}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name={item.key}
                    value="no"
                    checked={effects[item.key] === 'no'}
                    onChange={() => {
                      setEffects((current) => ({ ...current, [item.key]: 'no' }));
                      clearInvalid(effectId);
                    }}
                  />{' '}
                  No
                </label>
              </div>
              {isYesEffect(effects[item.key]) ? (
                <div className="side-effect-severity">
                  <label htmlFor={`severity-${item.key}`}>Severity</label>
                  <input
                    id={`severity-${item.key}`}
                    className="buy-again-slider"
                    type="range"
                    min="1"
                    max="5"
                    step="1"
                    value={effects[item.key]}
                    onChange={(e) =>
                      setEffects((current) => ({
                        ...current,
                        [item.key]: Number(e.target.value),
                      }))
                    }
                  />
                  <div className="buy-again-marks">
                    <span>1 Mild</span>
                    <span>3 Moderate</span>
                    <span>5 High</span>
                  </div>
                  <p className="field-note">
                    Selected: {effects[item.key]}
                    {SIDE_EFFECT_SEVERITY_LABELS[effects[item.key]]
                      ? ` — ${SIDE_EFFECT_SEVERITY_LABELS[effects[item.key]]}`
                      : ''}
                  </p>
                </div>
              ) : null}
            </div>
          );
        })}

        <button type="submit" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </form>
      <button type="button" className="secondary" onClick={onBack}>
        Back
      </button>
      <AchievementUnlockModal
        achievements={unlockedAchievements}
        onClose={() => {
          const logs = pendingLogs || [];
          setUnlockedAchievements([]);
          setPendingLogs(null);
          onSaved(logs);
        }}
      />
      <MissingFieldsModal
        messages={missingAlert?.messages || []}
        onClose={dismissMissingAlert}
      />
    </div>
  );
}

export default SmokeForm;
